import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Send, MessageCircle } from "lucide-react";
import { API } from "@/lib/admin-constants";

const SMSThread = ({ leadId }) => {
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const bottomRef = useRef(null);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const res = await axios.get(`${API}/sms/conversations/${leadId}`, { withCredentials: true });
        setMessages(res.data.messages || []);
      } catch (error) {
        console.error("Failed to fetch SMS messages:", error);
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    };
    fetchMessages();
    const interval = setInterval(fetchMessages, 10000);
    return () => clearInterval(interval);
  }, [leadId]);

  useEffect(() => {
    if (!initialLoad && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [messages, initialLoad]);

  const sendSMS = async () => {
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/sms/send/${leadId}`, { body: newMsg.trim() }, { withCredentials: true });
      setNewMsg("");
      const res = await axios.get(`${API}/sms/conversations/${leadId}`, { withCredentials: true });
      setMessages(res.data.messages || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send SMS");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="sms-thread">
      <div className="flex items-center gap-2 mb-2">
        <MessageCircle size={16} className="text-cfc-primary" />
        <span className="font-medium text-cfc-primary text-sm">SMS Conversation</span>
      </div>
      <div className="border border-cfc-border rounded-sm bg-white">
        <div className="max-h-48 overflow-y-auto p-3 space-y-2">
          {loading ? (
            <div className="flex justify-center py-4">
              <Loader2 size={16} className="animate-spin text-cfc-text-muted" />
            </div>
          ) : messages.length === 0 ? (
            <p className="text-xs text-cfc-text-muted text-center py-4">No SMS messages yet</p>
          ) : (
            messages.map((msg, idx) => (
              <div key={msg.message_id || idx} className={`flex ${msg.direction === "outbound" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${
                    msg.direction === "outbound"
                      ? "bg-cfc-primary text-white"
                      : "bg-cfc-cream text-cfc-primary"
                  }`}
                >
                  <p>{msg.body}</p>
                  <p className={`text-[10px] mt-1 ${
                    msg.direction === "outbound" ? "text-white/60" : "text-cfc-text-muted"
                  }`}>
                    {new Date(msg.timestamp).toLocaleString()}
                  </p>
                </div>
              </div>
            ))
          )}
          <div ref={bottomRef} />
        </div>
        <div className="border-t border-cfc-border p-2 flex gap-2">
          <Input
            value={newMsg}
            onChange={(e) => setNewMsg(e.target.value)}
            placeholder="Type a message..."
            className="text-sm"
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendSMS()}
            data-testid="sms-input"
          />
          <Button
            size="sm"
            onClick={sendSMS}
            disabled={sending || !newMsg.trim()}
            className="bg-cfc-primary hover:bg-cfc-primary-hover"
            data-testid="sms-send-btn"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SMSThread;
